import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { isNative } from '../lib/capacitor-bridge';
import { track } from '../lib/analytics';

const DONE_KEY = 'checkcheck_onboarding_completed';
const FIRST_VISIT_KEY = 'cc_first_visit_tracked';

const COPY = {
  fr: {
    steps: [
      {
        title: 'Avant de vous engager avec une entreprise, vérifiez-la.',
        sub: 'CheckCheck vous aide à comprendre rapidement sa situation financière.',
      },
      {
        title: 'Sur internet, tout le monde peut avoir l\u2019air solide. Les chiffres permettent de regarder plus loin.',
        sub: 'CheckCheck vous aide à regarder au-delà des apparences, à partir de données financières publiques.',
      },
      {
        title: 'Un beau site, de belles photos, de bons avis\u2026 ce n\u2019est pas toute l\u2019histoire.',
        sub: 'En quelques instants, CheckCheck vous donne une lecture claire de la situation financière d\u2019une entreprise.',
      },
      {
        title: 'Bienvenue sur CheckCheck',
        sub: 'CheckCheck vient de se lancer, et nous avons fait un choix simple : le rendre gratuit.',
        sub2: 'Vous pouvez aujourd\u2019hui profiter de l\u2019ensemble des fonctionnalités disponibles sans abonnement ni frais cachés. Des options premium pourront éventuellement être proposées plus tard, toujours de manière claire et transparente.',
      },
    ],
    next: 'Suivant',
    start: 'Commencer',
    progress: (i) => `Étape ${i} sur 4`,
  },
  nl: {
    steps: [
      {
        title: 'Controleer een bedrijf voordat u zich engageert.',
        sub: 'CheckCheck helpt u snel de financiële situatie te begrijpen.',
      },
      {
        title: 'Op internet kan iedereen er solide uitzien. Cijfers laten u verder kijken.',
        sub: 'CheckCheck helpt u voorbij de schijn te kijken, op basis van openbare financiële gegevens.',
      },
      {
        title: 'Een mooie website, mooie foto\u2019s, goede reviews\u2026 dat is niet het hele verhaal.',
        sub: 'In enkele ogenblikken geeft CheckCheck u een heldere lezing van de financiële situatie van een bedrijf.',
      },
      {
        title: 'Welkom bij CheckCheck',
        sub: 'CheckCheck is net gelanceerd, en we hebben een eenvoudige keuze gemaakt: het gratis maken.',
        sub2: 'U kunt vandaag alle beschikbare functies gebruiken zonder abonnement of verborgen kosten. Premiumopties kunnen later eventueel worden aangeboden, altijd op een duidelijke en transparante manier.',
      },
    ],
    next: 'Volgende',
    start: 'Beginnen',
    progress: (i) => `Stap ${i} van 4`,
  },
};

const shouldShow = () => {
  try {
    // QA devices réels : ?forceOnboarding=1 force l'affichage
    if (new URLSearchParams(window.location.search).get('forceOnboarding') === '1') return true;
    // Deep link rapport partagé : montrer immédiatement le contenu, jamais l'onboarding
    if (window.location.pathname.startsWith('/r/')) return false;
    // Outil de diagnostic réseau : JAMAIS masqué (utilisé sur devices vierges en test opérateur)
    if (window.location.pathname.startsWith('/network-check')) return false;
    if (localStorage.getItem(DONE_KEY) === 'true') return false;
    // NATIF : seul checkcheck_onboarding_completed fait foi. Une installation fraîche
    // affiche TOUJOURS les 4 écrans. Les clés héritées (ancienne intro native, token)
    // ne sont JAMAIS utilisées ici : une mise à jour TestFlight/Play conserve le
    // localStorage et les honorerait à tort (bug build 50). Les utilisateurs natifs
    // existants verront donc l'onboarding une seule fois après mise à jour (documenté).
    if (isNative()) return true;
    // WEB : ne pas imposer rétroactivement aux visiteurs existants
    // (ancien message de lancement déjà vu, ou compte présent sur ce navigateur)
    if (
      localStorage.getItem('ccc_launch_welcome_seen') === 'true' ||
      localStorage.getItem('ccc_token')
    ) {
      localStorage.setItem(DONE_KEY, 'true');
      return false;
    }
    return true;
  } catch {
    return false; // stockage indisponible : ne jamais bloquer l'accès au produit
  }
};

export const Onboarding = () => {
  const { lang } = useLanguage();
  const [visible, setVisible] = useState(shouldShow);
  const [step, setStep] = useState(0); // 0..3
  const ctaRef = useRef(null);

  // first_visit : une seule fois par device/navigateur
  useEffect(() => {
    try {
      if (localStorage.getItem(FIRST_VISIT_KEY) !== '1') {
        localStorage.setItem(FIRST_VISIT_KEY, '1');
        track('first_visit');
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (step === 0) track('onboarding_started');
    track(`onboarding_step_${step + 1}_seen`);
    if (step === 3) track('launch_message_seen');
    ctaRef.current?.focus();
  }, [visible, step]);

  const advance = useCallback(() => {
    if (step < 3) {
      setStep(step + 1);
      return;
    }
    try { localStorage.setItem(DONE_KEY, 'true'); } catch { /* noop */ }
    track('onboarding_completed');
    track('launch_message_continue');
    setVisible(false);
  }, [step]);

  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  if (!visible) return null;
  const c = COPY[lang] || COPY.fr;
  const s = c.steps[step];
  const isLast = step === 3;

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#102A43] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
      data-testid="onboarding"
    >
      <style>{`
        @keyframes obFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .ob-step { animation: obFadeUp 0.45s ease-out; }
        @media (prefers-reduced-motion: reduce) { .ob-step { animation: none; } }
      `}</style>

      {/* Halo bleu discret, cohérent avec l'identité */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-[18%] left-1/2 -translate-x-1/2 w-[130%] h-[55%] rounded-[100%]"
          style={{ background: 'radial-gradient(closest-side, rgba(37,99,235,0.25), rgba(37,99,235,0.08) 55%, transparent 75%)' }} />
      </div>

      <div className="relative flex-1 flex flex-col justify-center w-full max-w-md mx-auto px-8 min-h-0 overflow-y-auto">
        <img src="/logo192.png" alt="CheckCheck" className="w-12 h-12 rounded-xl mb-8 shrink-0" />
        <div key={step} className="ob-step">
          <h1
            id="onboarding-title"
            className={`text-white font-['Outfit'] font-extrabold leading-[1.15] tracking-tight ${isLast ? 'text-[clamp(1.6rem,7vw,2.2rem)]' : 'text-[clamp(1.4rem,6vw,2rem)]'}`}
            data-testid={`onboarding-title-${step + 1}`}
          >
            {s.title}
          </h1>
          <p className={`mt-5 leading-relaxed ${isLast ? 'text-white font-semibold text-[17px]' : 'text-slate-300 text-[16px]'}`} data-testid={`onboarding-sub-${step + 1}`}>
            {s.sub}
          </p>
          {s.sub2 && (
            <p className="mt-4 text-slate-300 text-[15px] leading-relaxed" data-testid="onboarding-sub2-4">
              {s.sub2}
            </p>
          )}
        </div>
      </div>

      <div className="relative w-full max-w-md mx-auto px-8 pb-8 pt-4 shrink-0">
        {/* Indicateur de progression : 4 points */}
        <div className="flex items-center justify-center gap-2 mb-5" role="status" aria-label={c.progress(step + 1)} data-testid="onboarding-progress">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              aria-hidden="true"
              className={`rounded-full transition-[width,background-color] duration-300 ${i === step ? 'w-6 h-2 bg-[#2563EB]' : 'w-2 h-2 bg-white/25'}`}
            />
          ))}
        </div>
        <button
          ref={ctaRef}
          onClick={advance}
          aria-label={isLast ? c.start : `${c.next} — ${c.progress(step + 1)}`}
          className="w-full h-14 rounded-full bg-[#2563EB] text-white font-bold text-base font-['Outfit'] shadow-[0_0_34px_rgba(37,99,235,0.4)] hover:bg-[#1D4ED8] active:scale-[0.98] transition-[background-color,transform] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4D8DFF]/50"
          data-testid={isLast ? 'onboarding-start' : 'onboarding-next'}
        >
          {isLast ? c.start : c.next}
        </button>
      </div>
    </div>
  );
};
